/**
 * The connector's issues as the dashboard shows them: its failed calls
 * bundled by tool and error kind (`GET /admin/mcp/issues`), each with a
 * status a developer gives it on its issue page or from Claude. The backend
 * owns the key, the category and the status; this module names them in the
 * dashboard's language, orders them and links to them.
 *
 *     <Chip tone={STATUS_TONE[issue.status]}>{issueStatusLabel(issue.status)}</Chip>
 *     <Link to={issuePath(issue.key, days)}>{issueKindLabel(issue.error_type)}</Link>
 */

import type { IssueCategory, IssueStatus, McpIssueDay, McpUsageDay } from "@/lib/api";
import type { ChipTone } from "@/lib/chips";
import { i18n } from "@/lib/i18n";
import { nl } from "@/lib/i18n/locales/nl";
import { type Period, PERIODS } from "@/lib/period";
import { toolLabel, type ToolTitles } from "@/lib/tool-titles";

/** Statistieken, and its first part, Overzicht. */
export const ANALYTICS_PATH = "/analytics";

/** The Oproepen part of Statistieken, with the call log. */
export const CALLS_PATH = "/analytics/oproepen";

/** The Fouten part of Statistieken, with the list of issues; an issue's page sits under it. */
export const FAILURES_PATH = "/analytics/fouten";

/** The Snelheid part of Statistieken. */
export const SPEED_PATH = "/analytics/snelheid";

/**
 * Every status, in the order a list sorts on it: what still needs a look
 * first (new, then come back after a fix), then what is open again, what a
 * developer is on, and what is settled.
 */
export const ISSUE_STATUSES: readonly IssueStatus[] = [
  "new",
  "regressed",
  "open",
  "acknowledged",
  "resolved",
  "ignored",
];

/** Every category, in the order the filter lists them. */
export const ISSUE_CATEGORIES: readonly IssueCategory[] = [
  "refused",
  "request",
  "odoo",
  "timeout",
  "cancelled",
  "internal",
];

/**
 * A status's place when a list sorts on it: new and come back share the
 * first place (what still needs a look, the most recent first when the list
 * breaks ties on the last failure), then the rest in `ISSUE_STATUSES`' order.
 */
export function statusRank(status: IssueStatus): number {
  return status === "regressed" ? statusRank("new") : ISSUE_STATUSES.indexOf(status);
}

/**
 * A status chip's colours: what still needs a look in solid De Ceuster blue
 * (new) or in the warning red (come back after a fix), open again in the
 * light tint, and what a developer is on or settled in grey.
 */
export const STATUS_TONE: Readonly<Record<IssueStatus, ChipTone>> = {
  new: "write",
  regressed: "danger",
  open: "read",
  acknowledged: "neutral",
  resolved: "neutral",
  ignored: "soon",
};

/** Whether an issue still waits for a developer's analysis: new, open, or come back. */
export function needsAnalysis(status: IssueStatus): boolean {
  return status === "new" || status === "open" || status === "regressed";
}

/** A status in words: "Nieuw", "Teruggekomen", "In behandeling". */
export function issueStatusLabel(status: IssueStatus): string {
  return i18n.t(`issues.status.${status}.label`);
}

/** What a status means, in a sentence, for its chip's tooltip. */
export function issueStatusHint(status: IssueStatus): string {
  return i18n.t(`issues.status.${status}.hint`);
}

/** A category in words: "Fout van Odoo", "Time-out". */
export function issueCategoryLabel(category: IssueCategory): string {
  return i18n.t(`issues.category.${category}`);
}

/** An error kind the texts name. */
type KnownKind = keyof typeof nl.issues.kinds;

const KNOWN_KINDS: ReadonlySet<string> = new Set(Object.keys(nl.issues.kinds));

/** Every error kind the texts name, as the backend records it (`odoo_slow`). */
export const ISSUE_KINDS: readonly string[] = [...KNOWN_KINDS];

/** The kinds the roadmap's rules refuse with start so, but the role's own refusal. */
const ROADMAP_RULE_PREFIX = "roadmap_";

/** An exception's class name, as the audit records an unexpected failure: `KeyError`, `ErrorResult`. */
const CLASS_NAME = /^[A-Z][A-Za-z0-9]*$/;

/**
 * An error kind in words, in the dashboard's language: "Odoo onbereikbaar"
 * for `odoo_unavailable`. A refusal by one of the roadmap's rules reads as
 * such, and an exception's class name (`KeyError`) as "Interne fout": the
 * issue's key, shown muted beside it, keeps the class name. Any other kind
 * the dashboard does not know shows as the backend recorded it.
 */
export function issueKindLabel(kind: string): string {
  if (KNOWN_KINDS.has(kind)) {
    return i18n.t(`issues.kinds.${kind as KnownKind}`);
  }
  if (kind.startsWith(ROADMAP_RULE_PREFIX)) {
    return i18n.t("issues.roadmapRule");
  }
  if (CLASS_NAME.test(kind)) {
    return i18n.t("issues.category.internal");
  }
  return kind;
}

/**
 * What an issue is about, in words: its tool's title (its name for a tool the
 * catalogue no longer lists), or "Hele connector" for a failure of the
 * connector as a whole.
 */
export function issueToolLabel(tool: string | null, titles: ToolTitles): string {
  return tool === null ? i18n.t("issues.wholeConnector") : toolLabel(titles, tool);
}

/**
 * An issue's page: `/analytics/fouten/<key>`, the key encoded (it holds a
 * colon, and a `*` for a failure of the connector as a whole), with the
 * period when it is not the default, so the page shows the same days.
 */
export function issuePath(key: string, days?: Period): string {
  const path = `${FAILURES_PATH}/${encodeURIComponent(key)}`;
  return days === undefined || days === PERIODS[0] ? path : `${path}?days=${days}`;
}

/**
 * An issue's failures per day as the calls chart takes them: its failures,
 * and the rest of the day's calls (of its tool, or of every tool for a
 * failure of the connector as a whole) as the successful ones, so the chart
 * can show them as a percentage of that day's calls.
 */
export function issueChartDays(days: readonly McpIssueDay[]): McpUsageDay[] {
  return days.map((day) => ({
    day: day.day,
    errors: day.count,
    ok: Math.max(0, day.calls - day.count),
  }));
}
